import React, { useState, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Chip from "@material-ui/core/Chip";
import Divider from "@material-ui/core/Divider";
import CircularProgress from "@material-ui/core/CircularProgress";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import SearchIcon from "@material-ui/icons/Search";
import StarIcon from "@material-ui/icons/Star";
import utils from "../../universal/utils";
import adminFetch from "../../universal/adminFetch";

const basePath = utils.getEdgeBasePath();

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  sectionTitle: {
    fontSize: "1.2rem",
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  searchRow: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "flex-end",
    marginBottom: theme.spacing(3),
  },
  resultCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
  },
  infoRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  grantRow: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "flex-end",
    marginTop: theme.spacing(2),
  },
  eventRow: {
    fontSize: "0.85rem",
  },
  tierChip: {
    fontWeight: 700,
    fontSize: "0.75rem",
  },
  tableContainer: {
    overflowX: "auto",
  },
}));

export default function MembershipManagement({ adminUserId }) {
  const classes = useStyles();

  const [targetUserId, setTargetUserId] = useState("");
  const [loading, setLoading] = useState(false);
  const [membershipData, setMembershipData] = useState(null);
  const [grantTier, setGrantTier] = useState("premium");
  const [grantDurationDays, setGrantDurationDays] = useState(365);
  const [alsoGrantFounder, setAlsoGrantFounder] = useState(false);
  const [message, setMessage] = useState(null);

  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [transfersLoading, setTransfersLoading] = useState(false);

  const fetchUserMembership = useCallback(async () => {
    if (!targetUserId || !adminUserId) return;
    setLoading(true);
    setMessage(null);

    try {
      const res = await adminFetch(
        `${basePath}/api/v7/admin/membership/user/${targetUserId}/adpass/${adminUserId}`
      );
      const data = await res.json();
      if (data.ok) {
        setMembershipData(data);
      } else {
        setMessage({ type: "error", text: data.err || "Failed to fetch membership" });
        setMembershipData(null);
      }
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setLoading(false);
    }
  }, [targetUserId, adminUserId]);

  const handleGrant = async () => {
    if (!targetUserId || !adminUserId) return;
    setLoading(true);
    setMessage(null);

    try {
      const res = await adminFetch(
        `${basePath}/api/v7/admin/membership/grant/adpass/${adminUserId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ targetUserId, tier: grantTier, durationDays: grantDurationDays }),
        }
      );
      const data = await res.json();
      if (data.ok) {
        let successText = `Granted ${grantTier} to user ${targetUserId} for ${grantDurationDays} day${grantDurationDays === 1 ? "" : "s"}`;

        if (alsoGrantFounder) {
          try {
            const founderRes = await adminFetch(
              `${basePath}/api/v7/admin/membership/founder-badge/grant/adpass/${adminUserId}`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ targetUserId }),
              }
            );
            const founderData = await founderRes.json();
            if (founderData.ok && founderData.granted) {
              successText += ` + Founder badge #${founderData.number}`;
            } else if (founderData.reason === "already_founder") {
              successText += " (already has Founder badge)";
            } else {
              successText += ` (Founder badge failed: ${founderData.reason || founderData.error || founderData.err || "unknown"})`;
            }
          } catch (founderErr) {
            successText += ` (Founder badge error: ${founderErr.message})`;
          }
        }

        setMessage({ type: "success", text: successText });
        fetchUserMembership();
      } else {
        setMessage({ type: "error", text: data.err || "Failed to grant tier" });
      }
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async () => {
    if (!targetUserId || !adminUserId) return;
    setLoading(true);
    setMessage(null);

    try {
      const res = await adminFetch(
        `${basePath}/api/v7/admin/membership/revoke/adpass/${adminUserId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ targetUserId }),
        }
      );
      const data = await res.json();
      if (data.ok) {
        setMessage({ type: "success", text: `Revoked membership for user ${targetUserId}` });
        fetchUserMembership();
      } else {
        setMessage({ type: "error", text: data.err || "Failed to revoke tier" });
      }
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingTransfers = async () => {
    if (!adminUserId) return;
    setTransfersLoading(true);

    try {
      const res = await adminFetch(
        `${basePath}/api/v7/admin/membership/pending-transfers/adpass/${adminUserId}`
      );
      const data = await res.json();
      if (data.ok) {
        setPendingTransfers(data.transfers || []);
      }
    } catch (err) {
      console.error("Failed to fetch pending transfers:", err);
    } finally {
      setTransfersLoading(false);
    }
  };

  const handleConfirmTransfer = async (referenceCode) => {
    try {
      const res = await adminFetch(
        `${basePath}/api/v7/admin/membership/confirm-transfer/adpass/${adminUserId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ referenceCode }),
        }
      );
      const data = await res.json();
      if (data.ok) {
        setMessage({ type: "success", text: `Transfer ${referenceCode} confirmed` });
        fetchPendingTransfers();
      } else {
        setMessage({ type: "error", text: data.err || "Failed to confirm transfer" });
      }
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    }
  };

  const handleRejectTransfer = async (referenceCode) => {
    try {
      const res = await adminFetch(
        `${basePath}/api/v7/admin/membership/reject-transfer/adpass/${adminUserId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ referenceCode }),
        }
      );
      const data = await res.json();
      if (data.ok) {
        setMessage({ type: "success", text: `Transfer ${referenceCode} rejected` });
        fetchPendingTransfers();
      } else {
        setMessage({ type: "error", text: data.err || "Failed to reject transfer" });
      }
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    }
  };

  const getTierColor = (tier) => {
    if (tier === "pro") return "#FFD700";
    if (tier === "premium") return "#FF7500";
    return "#8C8F8A";
  };

  return (
    <div className={classes.root}>
      {/* Status Message */}
      {message && (
        <Paper
          className={classes.resultCard}
          style={{
            borderColor: message.type === "success" ? "#4CAF50" : "#f44336",
            backgroundColor: message.type === "success" ? "rgba(76, 175, 80, 0.08)" : "rgba(244, 67, 54, 0.08)",
          }}
        >
          <Typography style={{ color: message.type === "success" ? "#4CAF50" : "#f44336" }}>
            {message.text}
          </Typography>
        </Paper>
      )}

      {/* User Membership Lookup */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>
          <StarIcon style={{ verticalAlign: "middle", marginRight: 8 }} />
          User Membership Lookup
        </Typography>

        <div className={classes.searchRow}>
          <TextField
            label="User ID"
            variant="outlined"
            size="small"
            value={targetUserId}
            onChange={(e) => setTargetUserId(e.target.value)}
            style={{ minWidth: 300 }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={16} /> : <SearchIcon />}
            onClick={fetchUserMembership}
            disabled={loading || !targetUserId}
          >
            Lookup
          </Button>
        </div>

        {membershipData && (
          <Paper className={classes.resultCard}>
            <div className={classes.infoRow}>
              <Typography variant="body2"><strong>Tier:</strong></Typography>
              <Chip
                label={(membershipData.membership?.tier || "free").toUpperCase()}
                size="small"
                className={classes.tierChip}
                style={{
                  backgroundColor: `${getTierColor(membershipData.membership?.tier)}22`,
                  color: getTierColor(membershipData.membership?.tier),
                  border: `1px solid ${getTierColor(membershipData.membership?.tier)}`,
                }}
              />
              {membershipData.membership?.grantedByAdmin && (
                <Chip label="Admin Granted" size="small" variant="outlined" />
              )}
            </div>
            {membershipData.membership?.grantedByAdmin && membershipData.subscription?.adminGrantDurationDays && (
              <div className={classes.infoRow}>
                <Typography variant="body2">
                  <strong>Grant Duration:</strong> {membershipData.subscription.adminGrantDurationDays} day{membershipData.subscription.adminGrantDurationDays === 1 ? "" : "s"}
                </Typography>
              </div>
            )}
            {membershipData.membership?.daysRemaining != null && (
              <div className={classes.infoRow}>
                <Typography variant="body2">
                  <strong>Days Remaining:</strong> {membershipData.membership.daysRemaining}
                </Typography>
              </div>
            )}
            {membershipData.membership?.expiresAt && (
              <div className={classes.infoRow}>
                <Typography variant="body2">
                  <strong>Expires:</strong> {new Date(membershipData.membership.expiresAt).toLocaleString()}
                </Typography>
              </div>
            )}

            <Divider style={{ margin: "12px 0" }} />

            {/* Grant/Revoke Controls */}
            <div className={classes.grantRow}>
              <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
                <InputLabel>Tier</InputLabel>
                <Select value={grantTier} onChange={(e) => setGrantTier(e.target.value)} label="Tier">
                  <MenuItem value="premium">Premium</MenuItem>
                  <MenuItem value="pro">Pro</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Duration (days)"
                type="number"
                variant="outlined"
                size="small"
                value={grantDurationDays}
                onChange={(e) => setGrantDurationDays(Math.max(1, Number(e.target.value) || 1))}
                inputProps={{ min: 1, max: 3650 }}
                style={{ width: 170 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={alsoGrantFounder}
                    onChange={(e) => setAlsoGrantFounder(e.target.checked)}
                    size="small"
                    style={{ color: "#C0A050" }}
                  />
                }
                label={<Typography variant="body2">Also grant Founder badge</Typography>}
              />
              <Button variant="contained" color="primary" onClick={handleGrant} disabled={loading}>
                Grant Tier
              </Button>
              <Button variant="outlined" color="secondary" onClick={handleRevoke} disabled={loading}>
                Revoke to Free
              </Button>
            </div>

            {/* Events Log */}
            {membershipData.events && membershipData.events.length > 0 && (
              <>
                <Divider style={{ margin: "16px 0" }} />
                <Typography variant="subtitle2" style={{ marginBottom: 8 }}>Membership Events</Typography>
                <div className={classes.tableContainer}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Event</TableCell>
                        <TableCell>From</TableCell>
                        <TableCell>To</TableCell>
                        <TableCell>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {membershipData.events.slice(0, 20).map((event, i) => (
                        <TableRow key={i}>
                          <TableCell className={classes.eventRow}>{event.eventType}</TableCell>
                          <TableCell className={classes.eventRow}>{event.fromTier}</TableCell>
                          <TableCell className={classes.eventRow}>{event.toTier}</TableCell>
                          <TableCell className={classes.eventRow}>
                            {new Date(event.createdAt).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </Paper>
        )}
      </div>

      <Divider style={{ marginBottom: 24 }} />

      {/* Pending Bank Transfers */}
      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>Pending Bank Transfers</Typography>
        <Button
          variant="outlined"
          onClick={fetchPendingTransfers}
          disabled={transfersLoading}
          startIcon={transfersLoading ? <CircularProgress size={16} /> : null}
          style={{ marginBottom: 16 }}
        >
          {transfersLoading ? "Loading..." : "Refresh Transfers"}
        </Button>

        {pendingTransfers.length === 0 && !transfersLoading && (
          <Typography variant="body2" color="textSecondary">
            No pending bank transfers found. Click Refresh to check.
          </Typography>
        )}

        {pendingTransfers.length > 0 && (
          <div className={classes.tableContainer}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User ID</TableCell>
                  <TableCell>Tier</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingTransfers.map((transfer) => (
                  <TableRow key={transfer.referenceCode}>
                    <TableCell className={classes.eventRow}>{transfer.userId}</TableCell>
                    <TableCell>
                      <Chip
                        label={(transfer.tier || "").toUpperCase()}
                        size="small"
                        style={{
                          backgroundColor: `${getTierColor(transfer.tier)}22`,
                          color: getTierColor(transfer.tier),
                        }}
                      />
                    </TableCell>
                    <TableCell className={classes.eventRow}>
                      {"\u20AC"}{transfer.amount?.toFixed(2)}
                    </TableCell>
                    <TableCell className={classes.eventRow}>
                      <code>{transfer.referenceCode}</code>
                    </TableCell>
                    <TableCell className={classes.eventRow}>
                      {new Date(transfer.createdAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        title="Confirm Transfer"
                        onClick={() => handleConfirmTransfer(transfer.referenceCode)}
                        style={{ color: "#4CAF50" }}
                      >
                        <CheckCircleIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        title="Reject Transfer"
                        onClick={() => handleRejectTransfer(transfer.referenceCode)}
                        style={{ color: "#f44336" }}
                      >
                        <CancelIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
